/* eslint-disable react-hooks/exhaustive-deps */
import React, { SyntheticEvent, ChangeEvent, useState, useEffect, useContext } from 'react';
import { AuthenticationContext } from 'services/context/authentication.context';
import CustomerContactService from 'services/customer-contact.service';
import { UserContact } from 'models/user-contact';
import Spinner from 'react-bootstrap/Spinner';
import { mapUserContactToCustomer } from 'services/user.service';
import { RouteProps } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import { validateBankAccountNumber, isValidCommaSeperatedEmail, isEmailValidAndPresent } from 'helpers/validators';
import { BANK_ACCOUNT_NUMBER_REGEX_PATTERN } from 'common/constants/regex';
import Select from 'react-select';
import { CustomerNavirecAccount } from 'models/customer';
import CustomerService from 'services/customer.service';
import { toast } from 'react-toastify';
import { isNotNullOrUndefined } from 'common/data-util';

interface UserProfileProps extends RouteProps {
  onLoadValidatePage: () => void;
  match: any;
}

export function UserProfile(props: UserProfileProps) {
  const [form, setForm] = useState({
    phone: '',
    email: '',
    bankAccountNumber: '',
    eInvoice: false,
    navirecApiEnabled: false,
    navirecToken: '',
    navirecAccountId: '',
    electricityInvoiceRecipientEmail: '',
    electricityInvoiceRecipientEmailEnabled: false,
    invoiceRecipientEmail: '',
    invoiceRecipientEmailEnabled: false
  })

  const [saving, setSaving] = useState(false);
  const [validated, setValidated] = useState(false);
  const [navirecAccounts, setNavirecAccounts] = useState<CustomerNavirecAccount[]>([]);
  const [isFetchingNavirec, setIsFetchingNavirec] = useState(false);
  const [isNavirecTokenInvalid, setIsNavirecTokenInvalid] = useState(false);

  const { state, dispatch } = useContext(AuthenticationContext);

  useEffect(() => {
    props.onLoadValidatePage();
  }, [props.match.path]);

  useEffect(() => {
    if(!state.customer) return;

    setForm({
      ...form,
      email: state.customer?.email,
      phone: state.customer?.phone,
      bankAccountNumber: state.customer?.bankAccountNumber,
      invoiceRecipientEmailEnabled: !!state.customer?.invoiceRecipientEmail,
      invoiceRecipientEmail: state.customer?.invoiceRecipientEmail,
      electricityInvoiceRecipientEmailEnabled: !!state.customer?.electricityInvoiceRecipientEmail,
      electricityInvoiceRecipientEmail: state.customer?.electricityInvoiceRecipientEmail,
      eInvoice: state.customer?.eInvoice,
      navirecApiEnabled: isNotNullOrUndefined(state.customer?.navirecToken),
      navirecAccountId: state.customer?.navirecAccountId,
      navirecToken: state.customer?.navirecToken
    })
  }, [state.customer]);

  useEffect(() => {
    const token = state.customer?.navirecToken;
    if(!token) return;

    fetchNavirecAccounts(token, true);
  }, [state.customer?.navirecToken]);

  const onSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setSaving(true);
    
    CustomerContactService.updateProfile(state.userId, state.customer.contactId, {
      email: form.email,
      phone: form.phone,
      bankAccountNumber: form.bankAccountNumber,
      eInvoice: form.eInvoice,
      electricityInvoiceRecipientEmail : form.electricityInvoiceRecipientEmailEnabled ? form.electricityInvoiceRecipientEmail : undefined,
      navirecToken : form.navirecApiEnabled ? form.navirecToken : undefined,
      navirecAccountId : form.navirecApiEnabled ? form.navirecAccountId : undefined
    }).then((res: UserContact) => {
      dispatch({
        type: "setCustomer",
        value: mapUserContactToCustomer(res)
      });
      setTimeout(() => setSaving(false), 500);
    }).catch(() => setSaving(false));
  }

  const onPhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.value && isNaN(Number(e.currentTarget.value)) && e.currentTarget.value !== '+') {
      return;
    }
    setForm({
      ...form,
      phone: e.currentTarget.value
    });
  }

  const handleNavirecTokenChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, navirecToken: value, navirecAccountId: '' }));
    setNavirecAccounts([]);
    setIsNavirecTokenInvalid(false);
  };

  const fetchNavirecAccounts = async (token: string, initial: boolean) => {
    setIsFetchingNavirec(true);

    try{
      const accounts = await CustomerService.findNavirecAccounts(
        state.customerId!,
        token
      )

      setNavirecAccounts(
        accounts
      )
      setIsNavirecTokenInvalid(false);

      if(accounts.length === 1) setForm((prev) => ({...prev, navirecAccountId: accounts[0].id}))

      if(!initial) toast("Kontode päring õnnestus", { type: 'success' });
    }catch(e){
      console.error(e)
      setNavirecAccounts([])
      setForm((prev) => ({...prev, navirecAccountId: ''}))
      toast("Kontode päring ebaõnnestus", { type: 'error' });
      if(!initial) setIsNavirecTokenInvalid(true);
    }finally{
      setIsFetchingNavirec(false)
    }
  }

  const validateForm = () => {
    setValidated(true);
    if (!isEmailValidAndPresent(form.email)) return false;

    if (form.electricityInvoiceRecipientEmailEnabled && (!form.electricityInvoiceRecipientEmail || !isValidCommaSeperatedEmail(form.electricityInvoiceRecipientEmail))) {
      return false;
    }

    if(form.navirecApiEnabled && (!form.navirecToken || !form.navirecAccountId)) return false;

    if(form.eInvoice){
      if(!form.bankAccountNumber){
          return false;
      }
      if(!validateBankAccountNumber(form.bankAccountNumber)){
          return false
      }
      return true;
    }

    return true;
  }

  const invoiceRecipientEmailIsInvalid = validated && form.electricityInvoiceRecipientEmailEnabled && (!form.electricityInvoiceRecipientEmail || !isValidCommaSeperatedEmail(form.electricityInvoiceRecipientEmail));
  const navirecAccountOptions = navirecAccounts.map(account => ({label: account.name, value: account.id})) ?? []
  const currentNavirecOption = navirecAccountOptions.find(option => option.value === form.navirecAccountId);

  return (
    <div className="container">
      <Form noValidate validated={validated}>
        <div className="row mt-3">
          <div className="col-12 col-md-6">
            <div className='form-row'>
              <div className='col'>
                <h2>
                { state.customer?.name } kontaktisiku andmed
                </h2>
              </div>
            </div>
            <div className="form-row">
              <Form.Group controlId={'form-email'} className={'col mb-0'} style={{ maxWidth: '300px' }}>
                  <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                      <span className="text-nowrap">E-posti aadress</span>
                  </Form.Label>
                  <Form.Control 
                      required
                      value={form.email}
                      onChange={(e) => setForm({
                        ...form,
                        email: e.currentTarget.value
                      })}
                      disabled={saving}
                      isInvalid={validated && !isEmailValidAndPresent(form.email)}
                    />
                  <Form.Control.Feedback type="invalid">E-posti aadress on kohustuslik</Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="form-row">
              <Form.Group controlId={'form-email'} className={'col mb-0'} style={{ maxWidth: '300px' }}>
                  <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                      <span className="text-nowrap">Telefon</span>
                  </Form.Label>
                  <Form.Control className={''}
                      value={form.phone}
                      onChange={onPhoneChange}
                      disabled={saving}
                      type="tel" />
              </Form.Group>
            </div>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12 col-md-6">
            <div className='form-row'>
              <div className='col'>
                <h2>
                  Arveldamine
                </h2>
              </div>
            </div>
            {
              form.invoiceRecipientEmailEnabled && (
                <>
                  <div className="form-row" style={{marginLeft : "1px"}}>
                    <Form.Check
                          id='check-invoice-recipient-email'
                          custom
                          type="checkbox"
                          className='checkbox-large'
                          label='Soovin arveid teisele e-posti aadressile'
                          onChange={() => setForm({ ...form, invoiceRecipientEmailEnabled: !form.electricityInvoiceRecipientEmailEnabled })}
                          checked
                          disabled
                      />
                  </div>
                  <div className="form-row">
                    <Form.Group controlId={'form-invoice-recipient-email'} className={'col mb-0'} style={{ maxWidth: '300px' }}>
                        <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                            <span className="text-nowrap">Arve saaja e-post</span>
                        </Form.Label>
                        <Form.Control
                          value={form.invoiceRecipientEmail}
                          onChange={(e) => setForm({...form, invoiceRecipientEmail: e.target.value})}
                          type='text'
                          required
                          isInvalid={invoiceRecipientEmailIsInvalid}
                          disabled
                          className='form-control-disabled-second'
                        />
                    </Form.Group>
                  </div>
                  <span className='text-gray-700 mt-1'>Arvete e-posti aadressi muutmiseks võtke palun ühendust klienditeenindusega.</span>
                </>
              )
            }
            <div className={`form-row ${form.invoiceRecipientEmailEnabled ? 'mt-2' : ''}`} style={{marginLeft : "1px"}}>
              <Form.Check
                    id='check-electricity-invoice-recipient-email'
                    custom
                    type="checkbox"
                    className='checkbox-large'
                    label='Soovin elektriarvet eraldi e-posti aadressile'
                    onChange={() => setForm({ ...form, electricityInvoiceRecipientEmailEnabled: !form.electricityInvoiceRecipientEmailEnabled })}
                    checked={form.electricityInvoiceRecipientEmailEnabled}
                />
            </div>
            {
              form.electricityInvoiceRecipientEmailEnabled && (
                <div className="form-row">
                  <Form.Group controlId={'form-electricity-invoice-recipient-email'} className={'col mb-0'} style={{ maxWidth: '300px' }}>
                      <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                          <span className="text-nowrap">Elektriarve saaja e-post</span>
                      </Form.Label>
                      <Form.Control
                        value={form.electricityInvoiceRecipientEmail}
                        onChange={(e) => setForm({...form, electricityInvoiceRecipientEmail: e.target.value})}
                        type='text'
                        required
                        isInvalid={invoiceRecipientEmailIsInvalid}
                      />
                      <Form.Control.Feedback type="invalid">E-posti aadress on vigane!</Form.Control.Feedback>
                  </Form.Group>
                </div>
              )
            }
            <div className="form-row mt-2" style={{marginLeft : "1px"}}>
              <Form.Check
                    id='check-e-invoice'
                    custom
                    type="checkbox"
                    className='checkbox-large'
                    label='Soovin e-arvet'
                    onChange={() => setForm({ ...form, eInvoice: !form.eInvoice })}
                    checked={form.eInvoice}
                />
            </div>
            {
              form.eInvoice && (
                <div className="form-row">
                  <Form.Group controlId={'form-bank-account-number'} className={'col mb-0'} style={{ maxWidth: '300px' }}>
                      <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                          <span className="text-nowrap">Pangakonto number</span>
                      </Form.Label>
                      <Form.Control
                        value={form.bankAccountNumber}
                        type="text"
                        onChange={(e) => setForm({...form, bankAccountNumber: e.target.value})}
                        placeholder="EE909900123456789012"
                        pattern={BANK_ACCOUNT_NUMBER_REGEX_PATTERN}
                        required={form.eInvoice}
                      />
                      <Form.Control.Feedback className={'text-right'} type="invalid">Pangakonto number ei ole valiidne</Form.Control.Feedback>
                  </Form.Group>
                </div>
              )
            }
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12 col-md-6">
            <div className='form-row'>
              <div className='col'>
                <h2>
                   Liidestused
                </h2>
              </div>
            </div>
            <div className="form-row" style={{marginLeft : "1px"}}>
              <Form.Check
                    id='check-navirec-api'
                    custom
                    type="checkbox"
                    className='checkbox-large'
                    label='Soovin liidestuda Navirec API-ga'
                    onChange={() => {
                      setForm({ ...form, navirecApiEnabled: !form.navirecApiEnabled })
                      if(form.navirecApiEnabled){
                        setNavirecAccounts([])
                      }
                    }}
                    checked={form.navirecApiEnabled}
                />
            </div>
            {
              form.navirecApiEnabled && (
                <>
                  <div className="form-row">
                    <Form.Group controlId={'form-navirec-token'} className={'col mb-0'}>
                        <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                            <span className="text-nowrap">API võti</span>
                        </Form.Label>
                        <div className='d-flex align-items-start'>
                          <div className='d-flex flex-column w-100' style={{ maxWidth: '290px' }}>
                            <Form.Control
                              required
                              value={form.navirecToken}
                              type="text"
                              onChange={handleNavirecTokenChange}
                              isInvalid={(validated && !form.navirecToken) || isNavirecTokenInvalid}
                            />
                            <Form.Control.Feedback type="invalid">API võti ei ole valiidne</Form.Control.Feedback>
                            { (validated && form.navirecToken && !navirecAccounts.length && !isNavirecTokenInvalid) && (<div className="d-block invalid-feedback">Päringu tegemine on kohustuslik</div>)}
                          </div>
                          <Button 
                            className="ml-2"
                            disabled={!form.navirecToken}
                            onClick={(e: any) => {
                              e.preventDefault();
                              fetchNavirecAccounts(form.navirecToken, false);
                            }}
                          >
                            {
                              isFetchingNavirec ? (
                                <Spinner size='sm' animation="border" className='p-0'/>
                              ) : (
                                  <div className='icon__refresh-white icon--16'/>
                              )
                            }
                          </Button>
                        </div>
                    </Form.Group>
                  </div>
                  {
                    !!navirecAccountOptions.length ? (
                      <div className="form-row">
                        <Form.Group controlId={'form-bank-account-number'} className={'col mb-0'} style={{ maxWidth: '300px' }}>
                          <Form.Label className={'mb-0 align-self-start m-1 text-center'}>
                              <span className="text-nowrap">Aktiivne konto</span>
                          </Form.Label>
                          <Select
                            options={navirecAccountOptions}
                            value={currentNavirecOption ?? null}
                            className={`select-terminal ${!form.navirecAccountId && validated ? 'invalid' : ''}`}
                            classNamePrefix={'select'}
                            onChange={(val : any) => setForm({
                                ...form,
                                navirecAccountId: val.value
                            })}
                            placeholder=""
                            noOptionsMessage={() => "Valikud puuduvad"}  
                            loadingMessage={() => "Laen andmeid..."}          
                          />
                          {
                              !form.navirecAccountId && validated && (
                                <div className="d-block invalid-feedback">Kohustuslik väli</div>
                              )
                          }
                        </Form.Group>
                      </div>
                    ) : null
                  }
                </>
              )
            }
          </div>
        </div>
        <div className="row mt-3">
          <div className="col">
            <Button className="px-4" onClick={onSubmit} disabled={saving}>
              {
                saving ?
                  <>
                    <span className='px-2'>Salvestab…</span>
                    <Spinner size={"sm"} animation={"border"} />
                  </> :
                  'Salvesta muudatused'
              }
            </Button>
          </div>
        </div>
      </Form>
    </div>
  )
}
